import { TituloDePantalla } from '@/shared/components/TituloDePantalla'
import { Badge } from '@/shared/components/Badge'
import { estiloDeBoton } from "@/shared/lib/estilosDeBoton";
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Search,
  Building,
  CircleUser,
  Link2,
  AlertCircle,
  Eye,
  MoreVertical,
  CheckCircle,
  XCircle,
  RotateCcw,
  FileSearch,
  RefreshCw,
  DollarSign,
} from "lucide-react";
import { apiService, JobRequestStatus } from '@/services/api';
import { conRechazo } from '@/features/rh/lib/rechazoDeSolicitudes';
import type { JobRequest } from '@/services/api';
import {
  generateCandidateRegistrationLink,
  copyLinkToClipboard,
} from '@/features/rh/lib/candidateLink';
import { useAuth } from '@/shared/hooks/useAuth';
import {
  canAssignJobRequestSalaries,
  canFinalApproveJobRequests,
  canPreApproveJobRequests,
  canRejectJobRequests,
  canPerformJobRequestActions,
  userHasPermission,
} from '@/shared/lib/roleUtils';
import { ActionModal } from '@/shared/components/ActionModal';
import { formatDateSafe } from '@/shared/lib/dateUtils';
import AssignSalariesModal from '@/features/rh/components/job-requests/modals/AssignSalariesModal';
import FinalApproveModal from '@/features/rh/components/job-requests/modals/FinalApproveModal';
import ReturnToRHModal from '@/features/rh/components/job-requests/modals/ReturnToRHModal';
import PermissionsDenyPage from '@/pages/configuracion/roles/permissionsDenyPage';
import { EsqueletoTarjetasTexto } from "@/shared/components/Esqueleto";
import { DatoEnRecuadro } from "@/features/rh/components/DatoEnRecuadro";
import { colorDePrioridad } from "@/features/rh/lib/prioridad";
import {
  formatJobRequestContractType,
  formatJobRequestPriority,
} from "@/features/rh/lib/jobRequestDisplay";

/**
 * Los colores del diseño de la bandeja.
 *
 * Van aquí con nombre y no sueltos entre las clases: son seis valores que se
 * repiten en la columna y en la tarjeta, y separados se desincronizan al primer
 * ajuste.
 */
const BORDE = "#E0E0E0";
const FONDO_COLUMNA = "#F7F9FA";
const FONDO_TARJETA = "#FDFDFE";
const SOMBRA_TARJETA = "0 1px 3px 1px rgba(0, 0, 0, 0.15)";
const TEXTO_TENUE = "#787878";

type JobRequestAction =
  | "review"
  | "approve"
  | "reject"
  | "return"
  | "assign_salaries"
  | "final_approve_salary"
  | "return_to_rh";

type JobRequestModalAction = "review" | "approve" | "reject" | "return";

const JobRequestsRHDashboard = () => {
  const navigate = useNavigate();
  const [jobRequests, setJobRequests] = useState<JobRequest[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [copiedLinkId, setCopiedLinkId] = useState<string | null>(null);
  const [selectedAction, setSelectedAction] = useState<{
    jobRequest: JobRequest;
    action: JobRequestModalAction;
  } | null>(null);
  const [openMenuId, setOpenMenuId] = useState<number | null>(null);
  const [assignSalariesModal, setAssignSalariesModal] = useState<{
    isOpen: boolean;
    jobRequest: JobRequest | null;
  }>({ isOpen: false, jobRequest: null });
  const [finalApproveModal, setFinalApproveModal] = useState<{
    isOpen: boolean;
    jobRequest: JobRequest | null;
  }>({ isOpen: false, jobRequest: null });
  const [returnToRHModal, setReturnToRHModal] = useState<{
    isOpen: boolean;
    jobRequest: JobRequest | null;
  }>({ isOpen: false, jobRequest: null });

  const { user } = useAuth();

  // Depende del id, no del objeto: `user` llega primero en null y luego con los
  // datos de la sesion, y con el objeto completo en las dependencias el listado
  // se volvia a cargar frente al usuario.
  useEffect(() => {
    if (user?.id) {
      loadJobRequests();
    }
  }, [user?.id]);

  const loadJobRequests = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const data = await apiService.getJobRequests();
      setJobRequests(data);
    } catch (errCrudo: unknown) {
      const err = errCrudo as {
        message?: string;
        statusCode?: number;
        status?: number;
        error?: string;
        response?: { status?: number; data?: { message?: string } };
      };
      setError(err.message || "Error al cargar las solicitudes de vacantes");
    } finally {
      setIsLoading(false);
    }
  };

  const canUserPerformActions = () => {
    return canPerformJobRequestActions(user || undefined);
  };

  canUserPerformActions();

  const getAvailableActions = (
    jobRequest: JobRequest,
  ): JobRequestAction[] => {
    if (!jobRequest || !canUserPerformActions()) {
      return [];
    }
    const canPreApprove = canPreApproveJobRequests(user || undefined);
    const canAssignSalaries = canAssignJobRequestSalaries(user || undefined);
    const canFinalApprove = canFinalApproveJobRequests(user || undefined);
    const canReturnInPreApproval =
      user?.rbac?.isMaster === true || user?.isAreaManager === true;
    const canReturnToRH = Boolean(
      user &&
        jobRequest?.solicitante &&
        (user.id === jobRequest.solicitante.id ||
          user.email === jobRequest.solicitante.email),
    );

    const canReject = canRejectJobRequests(user || undefined);
    let actions: JobRequestAction[] = [];

    switch (jobRequest.status) {
      case "pendiente_pre_aprobacion":
      case JobRequestStatus.PENDIENTE_VALIDACION_PM:
        actions = canPreApprove
          ? canReturnInPreApproval
            ? ["review", "approve", "return"]
            : ["review", "approve"]
          : [];
        break;
      case JobRequestStatus.PRE_APROBADA:
        actions = canAssignSalaries ? ["assign_salaries"] : [];
        break;
      case "pendiente_aprobacion_salario":
      case JobRequestStatus.EN_REVISION_SALARIOS:
      case JobRequestStatus.EN_REVISION:
        actions = canFinalApprove
          ? canReturnToRH
            ? ["final_approve_salary", "return_to_rh"]
            : ["final_approve_salary"]
          : [];
        break;
      case JobRequestStatus.DEVUELTA:
        actions = canPreApprove ? ["approve"] : [];
        break;
      default:
        actions = [];
        break;
    }

    // Rechazar sale solo con el permiso «Eliminar solicitudes».
    actions = conRechazo(actions, canReject, jobRequest.status);

    return actions;
  };

  const handleActionComplete = () => {
    setSelectedAction(null);
    loadJobRequests(); // Recargar la lista
  };

  const handleActionCancel = () => {
    setSelectedAction(null);
  };

  const handleAssignSalariesSuccess = (_updatedJobRequest: JobRequest) => {
    setAssignSalariesModal({ isOpen: false, jobRequest: null });
    loadJobRequests(); // Recargar la lista
  };

  const handleAssignSalariesCancel = () => {
    setAssignSalariesModal({ isOpen: false, jobRequest: null });
  };
  const handleFinalApproveSuccess = () => {
    setFinalApproveModal({ isOpen: false, jobRequest: null });
    loadJobRequests();
  };

  const handleFinalApproveCancel = () => {
    setFinalApproveModal({ isOpen: false, jobRequest: null });
  };

  const handleReturnToRHSuccess = () => {
    setReturnToRHModal({ isOpen: false, jobRequest: null });
    loadJobRequests();
  };

  const handleReturnToRHCancel = () => {
    setReturnToRHModal({ isOpen: false, jobRequest: null });
  };

  const handleActionClick = (
    e: React.MouseEvent,
    jobRequest: JobRequest,
    action: JobRequestAction,
  ) => {
    e.stopPropagation();
    if (action === "assign_salaries") {
      setAssignSalariesModal({ isOpen: true, jobRequest });
    } else if (action === "final_approve_salary") {
      setFinalApproveModal({ isOpen: true, jobRequest });
    } else if (action === "return_to_rh") {
      setReturnToRHModal({ isOpen: true, jobRequest });
    } else {
      setSelectedAction({ jobRequest, action });
    }
  };

  const toggleMenu = (e: React.MouseEvent, jobRequestId: number) => {
    e.stopPropagation();
    setOpenMenuId(openMenuId === jobRequestId ? null : jobRequestId);
  };

  useEffect(() => {
    const handleClickOutside = () => setOpenMenuId(null);
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, []);

  const handleCopyPublicLink = async (
    e: React.MouseEvent,
    jobRequest: JobRequest,
  ) => {
    e.stopPropagation();
    const publicLink = await generateCandidateRegistrationLink(jobRequest.id);
    const success = await copyLinkToClipboard(publicLink);
    if (success) {
      setCopiedLinkId(jobRequest.id.toString());
      setTimeout(() => setCopiedLinkId(null), 2000);
    }
  };

  // Filtro helper para búsqueda y plazasCubiertas
  const filterJobRequest = (jobRequest: JobRequest) => {
    // Filtro de búsqueda por texto
    const matchesSearch =
      (jobRequest.puestoSolicitar &&
        jobRequest.puestoSolicitar
          .toLowerCase()
          .includes(searchTerm.toLowerCase())) ||
      (jobRequest.areaVacante &&
        jobRequest.areaVacante
          .toLowerCase()
          .includes(searchTerm.toLowerCase())) ||
      (jobRequest.sede &&
        jobRequest.sede.toLowerCase().includes(searchTerm.toLowerCase()));

    if (!matchesSearch) return false;

    // Ocultar las que tienen plazasCubiertas: true
    if (jobRequest.plazasCubiertas === true) {
      return false;
    }

    return true;
  };

  // Agrupar por estado (aplicando filtros)
  const approvedRequests = jobRequests.filter(
    (jr) =>
      (jr.status === JobRequestStatus.APROBADA || jr.status === "aprobada") &&
      filterJobRequest(jr),
  );

  // Pendiente Pre-aprobación
  const pendingRequests = jobRequests.filter(
    (jr) =>
      (jr.status === JobRequestStatus.PRE_APROBADA ||
        jr.status === JobRequestStatus.EN_REVISION) &&
      filterJobRequest(jr),
  );

  // Pendiente Aprobación Salario: solo cuando RH ya asignó salarios
  const inReviewRequests = jobRequests.filter(
    (jr) =>
      (jr.status === JobRequestStatus.EN_REVISION_SALARIOS ||
        jr.status === "en_revision_salarios" ||
        jr.status === "pendiente_aprobacion_salario") &&
      filterJobRequest(jr),
  );
  // Devueltas: incluye DEVUELTA (legacy) + DEVUELTA_RH (nuevo flujo)
  const returnedRequests = jobRequests.filter(
    (jr) =>
      (jr.status === JobRequestStatus.DEVUELTA ||
        jr.status === JobRequestStatus.DEVUELTA_RH) &&
      filterJobRequest(jr),
  );

  if (isLoading) {
    return (
      <EsqueletoTarjetasTexto cuantas={8} columnas="md:grid-cols-2 xl:grid-cols-4" campos={4} />
    );
  }

  // Componente para una tarjeta de solicitud
  const JobRequestCard = ({
    jobRequest,
    showCandidateLink = false,
    hideActions = false,
    allowedActions,
  }: {
    jobRequest: JobRequest;
    showCandidateLink?: boolean;
    hideActions?: boolean;
    allowedActions?: JobRequestAction[];
  }) => {
    const availableActions = getAvailableActions(jobRequest);
    const visibleActions = allowedActions?.length
      ? availableActions.filter((action) => allowedActions.includes(action))
      : availableActions;
    const isMenuOpen = openMenuId === jobRequest.id;

    return (
      <div
        className="relative cursor-pointer rounded-xl p-3.5 transition-shadow hover:shadow-md"
        style={{ backgroundColor: FONDO_TARJETA, boxShadow: SOMBRA_TARJETA }}
        onClick={() => navigate(`/job-requests-rh/${jobRequest.id}`)}
      >
        {/* Estatus y prioridad, con el menú de acciones donde el diseño pone
            el ojo: es el único punto de entrada al flujo del tablero. */}
        <div className="flex items-start justify-between gap-2">
          <div className="flex min-w-0 flex-wrap items-center gap-x-3 gap-y-1">
            <span className="inline-flex items-center rounded-md bg-[#E3F8FF] px-2 py-0.5 text-[11px] font-medium text-[#2CA8D5]">
              Activa
            </span>
            {jobRequest.prioridad && (
              <span
                className="inline-flex items-center gap-1 text-[11px] font-medium"
                style={{ color: colorDePrioridad(jobRequest.prioridad) }}
              >
                <span aria-hidden>&bull;</span>
                Prioridad{" "}
                {formatJobRequestPriority(jobRequest.prioridad).toLowerCase()}
              </span>
            )}
          </div>
          <button
            onClick={(e) => toggleMenu(e, jobRequest.id)}
            className="-mr-1 -mt-1 flex-shrink-0 rounded p-1 transition-colors hover:bg-gray-100"
            title="Acciones"
          >
            <MoreVertical className="h-4 w-4 text-[#556AA1]" />
          </button>
        </div>

        {/* Menú desplegable de acciones */}
        {isMenuOpen && (
          <div className="absolute right-4 top-10 bg-white border border-gray-200 rounded-lg shadow-lg z-10 py-1 min-w-[140px]">
            {!hideActions && visibleActions.includes("review") && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleActionClick(e, jobRequest, "review");
                  setOpenMenuId(null);
                }}
                className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
              >
                <FileSearch className="w-4 h-4 text-blue-600" />
                Revisar
              </button>
            )}
            {!hideActions && visibleActions.includes("approve") && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleActionClick(e, jobRequest, "approve");
                  setOpenMenuId(null);
                }}
                className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
              >
                <CheckCircle className="w-4 h-4 text-green-600" />
                Aprobar
              </button>
            )}
            {!hideActions && visibleActions.includes("final_approve_salary") && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleActionClick(e, jobRequest, "final_approve_salary");
                    setOpenMenuId(null);
                  }}
                  className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                >
                  <CheckCircle className="w-4 h-4 text-green-600" />
                  Aprobar salario
                </button>
              )}
            {!hideActions && visibleActions.includes("reject") && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleActionClick(e, jobRequest, "reject");
                  setOpenMenuId(null);
                }}
                className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
              >
                <XCircle className="w-4 h-4 text-red-600" />
                Rechazar
              </button>
            )}
            {!hideActions && visibleActions.includes("return") && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleActionClick(e, jobRequest, "return");
                  setOpenMenuId(null);
                }}
                className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
              >
                <RotateCcw className="w-4 h-4 text-yellow-600" />
                Devolver
              </button>
            )}
            {!hideActions && visibleActions.includes("return_to_rh") && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleActionClick(e, jobRequest, "return_to_rh");
                  setOpenMenuId(null);
                }}
                className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
              >
                <RotateCcw className="w-4 h-4 text-yellow-600" />
                Devolver
              </button>
            )}
            {!hideActions && visibleActions.includes("assign_salaries") && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleActionClick(e, jobRequest, "assign_salaries");
                  setOpenMenuId(null);
                }}
                className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
              >
                <DollarSign className="w-4 h-4 text-green-600" />
                Asignar salarios
              </button>
            )}
            <button
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/job-requests-rh/${jobRequest.id}`);
                setOpenMenuId(null);
              }}
              className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
            >
              <Eye className="w-4 h-4 text-blue-600" />
              Ver detalles
            </button>
          </div>
        )}

        {/* Puesto */}
        <h3 className="mt-2.5 truncate text-sm font-semibold text-[#111111]">
          {jobRequest.puestoSolicitar || "Sin título"}
        </h3>

        {/* Área y quién la pidió */}
        <div
          className="mt-1.5 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs"
          style={{ color: TEXTO_TENUE }}
        >
          <span className="inline-flex min-w-0 items-center gap-1.5">
            <Building className="h-3.5 w-3.5 flex-shrink-0" />
            <span className="truncate">
              {jobRequest.areaVacante || "Sin área"}
            </span>
          </span>
          <span className="inline-flex min-w-0 items-center gap-1.5">
            <CircleUser className="h-3.5 w-3.5 flex-shrink-0" />
            <span className="truncate">
              {jobRequest?.solicitante?.firstName
                ? `${jobRequest.solicitante.firstName} ${
                    jobRequest.solicitante?.lastName ?? ""
                  }`.trim()
                : "Sin solicitante"}
            </span>
          </span>
        </div>

        {/* Los tres datos que se comparan de un vistazo entre solicitudes */}
        <div className="mt-3 grid grid-cols-[auto_auto_1fr] gap-1.5">
          <DatoEnRecuadro
            compacto
            etiqueta="Inicio"
            valor={formatDateSafe(
              jobRequest.fechaRequeridaInicio || jobRequest.fechaCreacion,
              "short",
            )}
          />
          <DatoEnRecuadro
            compacto
            etiqueta="Vacantes"
            valor={String(jobRequest.numeroPlazas ?? 1)}
          />
          <DatoEnRecuadro
            compacto
            etiqueta="Contrato"
            valor={formatJobRequestContractType(jobRequest.tipoContrato) || "Sin definir"}
          />
        </div>

        {/* Pie: cuándo se pidió y el enlace público, separados por una línea */}
        <div
          className="mt-3 flex flex-wrap items-center justify-between gap-x-2 gap-y-2 border-t pt-3"
          style={{ borderColor: BORDE }}
        >
          <p className="text-[11px] leading-tight" style={{ color: TEXTO_TENUE }}>
            Solicitada:{" "}
            <span className="text-[#121212]">
              {formatDateSafe(jobRequest.fechaCreacion, "short")}
            </span>
            <span className="ml-2">ID: </span>
            <span className="text-[#121212]">{jobRequest.id}</span>
          </p>

          {/* Solo en Vacante activa: antes de que la vacante se publique el
              enlace lleva a una página sin puesto que mostrar. */}
          {!hideActions && showCandidateLink && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleCopyPublicLink(e, jobRequest);
              }}
              className={estiloDeBoton('secundario', 'flex-shrink-0')}
            >
              {copiedLinkId === jobRequest.id.toString() ? (
                <>
                  <CheckCircle className="h-4 w-4 text-green-600" />
                  Copiado
                </>
              ) : (
                <>
                  <Link2 className="h-4 w-4 text-[#556AA1]" />
                  Link de candidatos
                </>
              )}
            </button>
          )}
        </div>
      </div>
    );
  };

  // Componente para una columna del Kanban
  const KanbanColumn = ({
    title,
    count,
    requests,
    emptyMessage,
    showCandidateLink = false,
    hideActions = false,
    allowedActions,
  }: {
    title: string;
    count: number;
    requests: JobRequest[];
    emptyMessage?: string;
    showCandidateLink?: boolean;
    hideActions?: boolean;
    allowedActions?: JobRequestAction[];
  }) => (
    /* Cada columna es su propio contenedor con borde: sin él las cuatro se
       leían como una sola superficie y no se veía dónde terminaba cada una. */
    <div
      className="flex min-h-[500px] flex-col overflow-hidden rounded-2xl border"
      style={{ borderColor: BORDE, backgroundColor: FONDO_COLUMNA }}
    >
      <div
        className="flex items-center justify-between gap-2 border-b bg-white px-4 py-3"
        style={{ borderColor: BORDE }}
      >
        <h2 className="min-w-0 flex-1 truncate text-sm font-semibold text-gray-900">
          {title}
        </h2>
        {/* En azul cuando hay algo que atender; en gris cuando la columna está
            vacía, para que el cero no compita con las que sí tienen trabajo. */}
        <Badge color={count > 0 ? "morado" : "gris"} className="flex-shrink-0">
          {count}
        </Badge>
      </div>

      <div className="flex-1 space-y-3 p-3">
        {requests.length === 0 ? (
          <p className="py-8 text-center text-xs" style={{ color: TEXTO_TENUE }}>
            {emptyMessage || "No hay solicitudes en esta categoría"}
          </p>
        ) : (
          requests.map((request) => (
            <JobRequestCard
              key={request.id}
              jobRequest={request}
              showCandidateLink={showCandidateLink}
              hideActions={hideActions}
              allowedActions={allowedActions}
            />
          ))
        )}
      </div>
    </div>
  );

  if (
    userHasPermission(
      "rh.gestion_solicitudes.base.read",
      user?.rbac?.effectivePermissions || [],
    ) === false &&
    user?.isAreaManager !== true &&
    !isLoading
  ) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <PermissionsDenyPage />
      </div>
    );
  }

  return (
    <div className="space-y-6 mt-8 mb-6">
      {/* Header. El buscador va debajo de la descripción, no junto al título:
          así el encabezado se lee de corrido y la fila de abajo queda para lo
          que se hace con la pantalla. */}
      <div className="space-y-4">
        <div>
          <TituloDePantalla>
            Bandeja de solicitudes
          </TituloDePantalla>
          <p className="mt-1 text-sm text-[#787878]">
            Da seguimiento a las solicitudes de personal desde la asignación de
            salario hasta la publicación de la vacante.
          </p>
        </div>

        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="relative w-full sm:max-w-[420px]">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Bandeja de solicitudes..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full rounded-lg border py-2 pl-9 pr-4 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              style={{ borderColor: BORDE }}
            />
          </div>

          <button
            onClick={loadJobRequests}
            className={estiloDeBoton("primario")}
          >
            <RefreshCw className="h-4 w-4" />
            Actualizar
          </button>
        </div>
      </div>

      {/* Error Alert */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center">
            <AlertCircle className="w-5 h-5 text-red-500 mr-2" />
            <span className="text-red-700">{error}</span>
          </div>
        </div>
      )}

      {/* Kanban Board */}
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-4">
        <KanbanColumn
          title="Pendiente asignar salarios"
          count={pendingRequests.length}
          requests={pendingRequests}
          emptyMessage="No hay solicitudes pendientes"
          allowedActions={["assign_salaries"]}
        />
        <KanbanColumn
          title="Pendiente aprobación de salario"
          count={inReviewRequests.length}
          requests={inReviewRequests}
          emptyMessage="No hay solicitudes en revisión"
        />
        <KanbanColumn
          title="Devuelta a RH"
          count={returnedRequests.length}
          requests={returnedRequests}
          emptyMessage="No hay solicitudes devueltas"
        />
        <KanbanColumn
          title="Vacante activa"
          count={approvedRequests.length}
          requests={approvedRequests}
          emptyMessage="No hay solicitudes abiertas"
          showCandidateLink={true}
        />
      </div>

      {/* Modal de Acciones */}
      {selectedAction && (
        <ActionModal
          isOpen={true}
          action={selectedAction.action}
          requestId={selectedAction.jobRequest.id.toString()}
          onSuccess={handleActionComplete}
          onCancel={handleActionCancel}
        />
      )}

      {/* Modal de Asignación de Salarios */}
      {assignSalariesModal.jobRequest && (
        <AssignSalariesModal
          jobRequest={assignSalariesModal.jobRequest}
          isOpen={assignSalariesModal.isOpen}
          onClose={handleAssignSalariesCancel}
          onSuccess={handleAssignSalariesSuccess}
        />
      )}
      {finalApproveModal.jobRequest && (
        <FinalApproveModal
          jobRequest={finalApproveModal.jobRequest}
          isOpen={finalApproveModal.isOpen}
          onClose={handleFinalApproveCancel}
          onSuccess={handleFinalApproveSuccess}
        />
      )}
      {returnToRHModal.jobRequest && (
        <ReturnToRHModal
          jobRequest={returnToRHModal.jobRequest}
          isOpen={returnToRHModal.isOpen}
          onClose={handleReturnToRHCancel}
          onSuccess={handleReturnToRHSuccess}
        />
      )}
    </div>
  );
};

export default JobRequestsRHDashboard;
